import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useOptionalInternalWebRTC } from "../../../internal-contacts/_context/InternalWebRTCContext";
import {
  useOptionalVirtualAssistant,
  type CompanyRuntimeState,
} from "../../context/VirtualAssistantContext";
import { ConcurrentCallChoice } from "./ConcurrentCallChoice";

export type HeldSide = "webrtc" | "pstn" | null;

export type ArbitrationScenario =
  | "pstn-incoming-during-webrtc"
  | "webrtc-incoming-during-pstn"
  | null;

export interface CallArbitrationValue {
  scenario: ArbitrationScenario;
  showChoice: boolean;
  alternating: boolean;
  heldSide: HeldSide;
  currentLabel: string;
  incomingLabel: string;
  pstnActiveKey: string | null;
  webrtcLabel: string;
  pstnLabel: string;
  answerNewEndCurrent: () => void;
  holdCurrentAnswerNew: () => void;
  hangupIncoming: () => void;
  switchActive: () => void;
}

const CallArbitrationContext = createContext<CallArbitrationValue | null>(null);

export const useOptionalCallArbitration = (): CallArbitrationValue | null =>
  useContext(CallArbitrationContext);

const formatPhoneLabel = (
  remotePhone: { dialCode?: string | null; phone?: string | null } | null | undefined,
): string => {
  if (!remotePhone?.phone) return "Incoming call";
  const dialCode = remotePhone.dialCode?.trim() ?? "";
  return `${dialCode}${remotePhone.phone}`.trim();
};

const findActivePstnEntry = (
  companiesState: Map<string, CompanyRuntimeState>,
): { key: string; state: CompanyRuntimeState } | null => {
  for (const [key, state] of companiesState.entries()) {
    if (state.callDirection === "OUTGOING" || (state.activeCall && state.callDirection !== "INCOMING")) {
      return { key, state };
    }
  }
  return null;
};

export const CallArbitrationProvider = ({ children }: { children: ReactNode }) => {
  const internal = useOptionalInternalWebRTC();
  const voip = useOptionalVirtualAssistant();

  const [alternating, setAlternating] = useState(false);
  const [heldSide, setHeldSide] = useState<HeldSide>(null);
  const [dismissedId, setDismissedId] = useState<string | null>(null);

  const webrtcActive = internal?.internalCallView === "in-call";
  const webrtcIncoming = internal?.internalCallView === "incoming";
  const webrtcPresent =
    !!internal && internal.phase !== "idle" && internal.phase !== "ended";
  const webrtcEngaged =
    !!internal &&
    (internal.phase === "outgoing-ringing" ||
      internal.phase === "connecting" ||
      internal.phase === "in-call");
  const incomingPstn = voip?.incomingCalls?.[0] ?? null;

  const pstnActiveEntry = useMemo(
    () => (voip ? findActivePstnEntry(voip.companiesState) : null),
    [voip],
  );
  const pstnActive = !!pstnActiveEntry;

  const pstnCallPresent = useMemo(() => {
    if (!voip) return false;
    for (const [, state] of voip.companiesState.entries()) {
      if (state.activeCall || state.callDirection === "OUTGOING") return true;
    }
    return false;
  }, [voip]);

  const scenario: ArbitrationScenario =
    webrtcActive && incomingPstn
      ? "pstn-incoming-during-webrtc"
      : pstnActive && webrtcIncoming
        ? "webrtc-incoming-during-pstn"
        : null;

  const incomingId =
    scenario === "pstn-incoming-during-webrtc"
      ? incomingPstn?.key ?? null
      : scenario === "webrtc-incoming-during-pstn"
        ? internal?.incomingCall?.callUid ?? null
        : null;

  const pstnActiveKey = pstnActiveEntry?.key ?? null;
  const pstnIncomingKey = incomingPstn?.key ?? null;

  const showChoice =
    !!scenario && !alternating && !!incomingId && dismissedId !== incomingId;

  useEffect(() => {
    if (!scenario && !alternating && dismissedId !== null) {
      setDismissedId(null);
    }
  }, [scenario, alternating, dismissedId]);

  useEffect(() => {
    if (!alternating) return;
    if (webrtcPresent && pstnCallPresent) return;

    if (webrtcPresent && heldSide === "webrtc") {
      void internal?.resume();
    }
    if (pstnActive && heldSide === "pstn" && pstnActiveKey) {
      voip?.toggleHoldByCompany(pstnActiveKey);
    }
    setAlternating(false);
    setHeldSide(null);
  }, [alternating, webrtcPresent, pstnCallPresent, pstnActive, heldSide, pstnActiveKey, internal, voip]);

  const prevWebrtcEngagedRef = useRef(false);
  const prevPstnEngagedRef = useRef(false);

  useEffect(() => {
    const webrtcJustEngaged = webrtcEngaged && !prevWebrtcEngagedRef.current;
    const pstnJustEngaged = pstnActive && !prevPstnEngagedRef.current;
    prevWebrtcEngagedRef.current = webrtcEngaged;
    prevPstnEngagedRef.current = pstnActive;

    if (alternating) return;
    if (!webrtcEngaged || !pstnActive) return;

    const holdPstn = () => {
      if (!pstnActiveKey || !voip) return;
      const state = voip.getCompanyCallState(pstnActiveKey);
      if (state && !state.isOnHold) voip.toggleHoldByCompany(pstnActiveKey);
    };

    if (pstnJustEngaged && !webrtcJustEngaged) {
      void internal?.hold();
      setHeldSide("webrtc");
    } else {
      holdPstn();
      setHeldSide("pstn");
    }
    setAlternating(true);
  }, [webrtcEngaged, pstnActive, alternating, pstnActiveKey, internal, voip]);

  const currentLabel =
    scenario === "webrtc-incoming-during-pstn"
      ? formatPhoneLabel(pstnActiveEntry?.state.remotePhone)
      : internal?.peerDisplayName || "Internal call";

  const incomingLabel =
    scenario === "pstn-incoming-during-webrtc"
      ? formatPhoneLabel(incomingPstn?.remotePhone)
      : internal?.peerDisplayName || "Internal call";

  const webrtcLabel = internal?.peerDisplayName || "Internal call";
  const pstnLabel = formatPhoneLabel(pstnActiveEntry?.state.remotePhone);

  const answerNewEndCurrent = useCallback(() => {
    if (scenario === "pstn-incoming-during-webrtc" && pstnIncomingKey) {
      void (async () => {
        await internal?.hold();
        voip?.acceptCallByCompany(pstnIncomingKey);
        void internal?.hangup();
      })();
    } else if (scenario === "webrtc-incoming-during-pstn" && pstnActiveKey) {
      voip?.hangupCallByCompany(pstnActiveKey);
      void internal?.acceptIncoming();
    }
    setDismissedId(null);
  }, [scenario, pstnIncomingKey, pstnActiveKey, internal, voip]);

  const holdCurrentAnswerNew = useCallback(() => {
    if (scenario === "pstn-incoming-during-webrtc" && pstnIncomingKey) {
      void (async () => {
        await internal?.hold();
        await voip?.acceptCallByCompany(pstnIncomingKey);
        setHeldSide("webrtc");
        setAlternating(true);
      })();
    } else if (scenario === "webrtc-incoming-during-pstn" && pstnActiveKey) {
      voip?.toggleHoldByCompany(pstnActiveKey);
      void internal?.acceptIncoming();
      setHeldSide("pstn");
      setAlternating(true);
    }
  }, [scenario, pstnIncomingKey, pstnActiveKey, internal, voip]);

  const hangupIncoming = useCallback(() => {
    if (scenario === "pstn-incoming-during-webrtc" && pstnIncomingKey) {
      voip?.hangupCallByCompany(pstnIncomingKey);
    } else if (scenario === "webrtc-incoming-during-pstn") {
      void internal?.rejectIncoming();
    }
    setDismissedId(null);
  }, [scenario, pstnIncomingKey, internal, voip]);

  const switchActive = useCallback(() => {
    if (!alternating || !pstnActiveKey) return;

    if (heldSide === "webrtc") {
      voip?.toggleHoldByCompany(pstnActiveKey);
      void internal?.resume();
      setHeldSide("pstn");
    } else if (heldSide === "pstn") {
      void (async () => {
        await internal?.hold();
        voip?.toggleHoldByCompany(pstnActiveKey);
        setHeldSide("webrtc");
      })();
    }
  }, [alternating, heldSide, pstnActiveKey, internal, voip]);

  const value = useMemo<CallArbitrationValue>(
    () => ({
      scenario,
      showChoice,
      alternating,
      heldSide,
      currentLabel,
      incomingLabel,
      pstnActiveKey,
      webrtcLabel,
      pstnLabel,
      answerNewEndCurrent,
      holdCurrentAnswerNew,
      hangupIncoming,
      switchActive,
    }),
    [
      scenario,
      showChoice,
      alternating,
      heldSide,
      currentLabel,
      incomingLabel,
      pstnActiveKey,
      webrtcLabel,
      pstnLabel,
      answerNewEndCurrent,
      holdCurrentAnswerNew,
      hangupIncoming,
      switchActive,
    ],
  );

  return (
    <CallArbitrationContext.Provider value={value}>
      {children}
      {showChoice && <ConcurrentCallChoice />}
    </CallArbitrationContext.Provider>
  );
};
